import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  LayoutTemplate, Plus, Save, Eye, ArrowLeft, Trash2, GripVertical,
  Type, Image, MousePointerClick, HelpCircle, Star, Code, ChevronUp, ChevronDown, Loader2,
} from "lucide-react";

const BLOCK_TYPES = [
  { type: "hero", label: "Hero Banner", icon: Image, desc: "Full-width banner with heading, text & CTA" },
  { type: "text", label: "Text Block", icon: Type, desc: "Rich text content section" },
  { type: "gallery", label: "Image Gallery", icon: Image, desc: "Grid of images" },
  { type: "cta", label: "Call to Action", icon: MousePointerClick, desc: "Button with heading & description" },
  { type: "faq", label: "FAQ Section", icon: HelpCircle, desc: "Accordion Q&A" },
  { type: "testimonials", label: "Testimonials", icon: Star, desc: "Customer quotes" },
  { type: "html", label: "Custom HTML", icon: Code, desc: "Raw HTML embed" },
];

function BlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const t = block.type;
  const c = block.content || {};
  const set = (k, v) => onChange({ ...block, content: { ...c, [k]: v } });

  return (
    <Card className="p-4 border-l-4 border-l-primary/50" data-testid={`block-${block.order}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-muted-foreground" />
          <Badge variant="outline" className="capitalize text-xs">{t}</Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onMoveUp} disabled={isFirst}><ChevronUp className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onMoveDown} disabled={isLast}><ChevronDown className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={onRemove}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      {t === "hero" && (
        <div className="space-y-3">
          <div><Label>Heading</Label><Input value={c.heading || ""} onChange={(e) => set("heading", e.target.value)} placeholder="Welcome to Hyderabad" data-testid="block-hero-heading" /></div>
          <div><Label>Subheading</Label><Input value={c.subheading || ""} onChange={(e) => set("subheading", e.target.value)} placeholder="Discover the City of Pearls" /></div>
          <div><Label>Background Image URL</Label><Input value={c.bg_image || ""} onChange={(e) => set("bg_image", e.target.value)} placeholder="https://..." /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>CTA Text</Label><Input value={c.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)} placeholder="Explore Tours" /></div>
            <div><Label>CTA Link</Label><Input value={c.cta_link || ""} onChange={(e) => set("cta_link", e.target.value)} placeholder="/tours" /></div>
          </div>
        </div>
      )}

      {t === "text" && (
        <div className="space-y-3">
          <div><Label>Heading (optional)</Label><Input value={c.heading || ""} onChange={(e) => set("heading", e.target.value)} /></div>
          <div><Label>Content (Markdown supported)</Label><Textarea rows={6} value={c.body || ""} onChange={(e) => set("body", e.target.value)} data-testid="block-text-body" /></div>
        </div>
      )}

      {t === "gallery" && (
        <div className="space-y-3">
          <div><Label>Heading</Label><Input value={c.heading || ""} onChange={(e) => set("heading", e.target.value)} /></div>
          <div><Label>Image URLs (one per line)</Label><Textarea rows={4} value={(c.images || []).join("\n")} onChange={(e) => set("images", e.target.value.split("\n").filter(Boolean))} placeholder="https://image1.jpg&#10;https://image2.jpg" /></div>
        </div>
      )}

      {t === "cta" && (
        <div className="space-y-3">
          <div><Label>Heading</Label><Input value={c.heading || ""} onChange={(e) => set("heading", e.target.value)} placeholder="Ready to explore?" /></div>
          <div><Label>Description</Label><Input value={c.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Button Text</Label><Input value={c.button_text || ""} onChange={(e) => set("button_text", e.target.value)} placeholder="Book Now" /></div>
            <div><Label>Button Link</Label><Input value={c.button_link || ""} onChange={(e) => set("button_link", e.target.value)} placeholder="/search" /></div>
          </div>
          <div><Label>Background Color</Label><Input value={c.bg_color || ""} onChange={(e) => set("bg_color", e.target.value)} placeholder="#006666" /></div>
        </div>
      )}

      {t === "faq" && (
        <div className="space-y-3">
          <div><Label>Heading</Label><Input value={c.heading || ""} onChange={(e) => set("heading", e.target.value)} placeholder="Frequently Asked Questions" /></div>
          <div><Label>Items (JSON: [{`{"q":"...","a":"..."}`}])</Label>
            <Textarea rows={5} value={c.items_json || "[]"} onChange={(e) => set("items_json", e.target.value)} placeholder='[{"q":"Question?","a":"Answer"}]' />
          </div>
        </div>
      )}

      {t === "testimonials" && (
        <div className="space-y-3">
          <div><Label>Heading</Label><Input value={c.heading || ""} onChange={(e) => set("heading", e.target.value)} placeholder="What our guests say" /></div>
          <div><Label>Testimonials (JSON: [{`{"name":"...","text":"...","rating":5}`}])</Label>
            <Textarea rows={5} value={c.items_json || "[]"} onChange={(e) => set("items_json", e.target.value)} />
          </div>
        </div>
      )}

      {t === "html" && (
        <div><Label>HTML</Label><Textarea rows={8} value={c.html || ""} onChange={(e) => set("html", e.target.value)} className="font-mono text-xs" data-testid="block-html" /></div>
      )}
    </Card>
  );
}

export default function AdminPageBuilder() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const isNew = pageId === "new";

  const [page, setPage] = useState({
    title: "", slug: "", status: "draft", blocks: [], seo_title: "", seo_description: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  useEffect(() => {
    if (!isNew && pageId) {
      api.get(`/admin/pages/${pageId}`)
        .then((r) => setPage(r.data))
        .catch(() => toast.error("Page not found"))
        .finally(() => setLoading(false));
    }
  }, [pageId, isNew]);

  const addBlock = (type) => {
    const newBlock = { type, content: {}, order: page.blocks.length };
    setPage({ ...page, blocks: [...page.blocks, newBlock] });
  };

  const updateBlock = (idx, updated) => {
    const blocks = [...page.blocks];
    blocks[idx] = updated;
    setPage({ ...page, blocks });
  };

  const removeBlock = (idx) => {
    const blocks = page.blocks.filter((_, i) => i !== idx).map((b, i) => ({ ...b, order: i }));
    setPage({ ...page, blocks });
  };

  const moveBlock = (idx, dir) => {
    const blocks = [...page.blocks];
    const target = idx + dir;
    if (target < 0 || target >= blocks.length) return;
    [blocks[idx], blocks[target]] = [blocks[target], blocks[idx]];
    setPage({ ...page, blocks: blocks.map((b, i) => ({ ...b, order: i })) });
  };

  const save = async () => {
    if (!page.title || !page.slug) { toast.error("Title and slug required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post("/admin/pages", page);
        toast.success("Page created");
        navigate(`/admin/pages/${res.data.id}`, { replace: true });
      } else {
        await api.put(`/admin/pages/${pageId}`, page);
        toast.success("Page saved");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-12"><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="page-builder">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/pages")} data-testid="back-to-pages"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="font-display text-2xl flex items-center gap-2">
              <LayoutTemplate className="w-6 h-6 text-primary" />
              {isNew ? "New Page" : "Edit Page"}
            </h1>
          </div>
        </div>
        <div className="flex gap-2">
          {page.slug && page.status === "published" && (
            <Button variant="outline" size="sm" onClick={() => window.open(`/p/${page.slug}`, "_blank")} data-testid="preview-page">
              <Eye className="w-4 h-4 mr-1" /> Preview
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="save-page">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      {/* Page meta */}
      <Card className="p-5 mb-6">
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label>Page Title *</Label>
            <Input value={page.title} onChange={(e) => setPage({ ...page, title: e.target.value })} data-testid="page-title" />
          </div>
          <div>
            <Label>URL Slug *</Label>
            <Input value={page.slug} onChange={(e) => setPage({ ...page, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="page-slug" />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={page.status} onValueChange={(v) => setPage({ ...page, status: v })}>
              <SelectTrigger data-testid="page-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div><Label>SEO Title</Label><Input value={page.seo_title || ""} onChange={(e) => setPage({ ...page, seo_title: e.target.value })} maxLength={60} /></div>
          <div><Label>Meta Description</Label><Input value={page.seo_description || ""} onChange={(e) => setPage({ ...page, seo_description: e.target.value })} maxLength={160} /></div>
        </div>
      </Card>

      {/* Blocks */}
      <div className="space-y-3 mb-6" data-testid="blocks-list">
        {page.blocks.map((block, idx) => (
          <BlockEditor
            key={idx}
            block={block}
            onChange={(b) => updateBlock(idx, b)}
            onRemove={() => removeBlock(idx)}
            onMoveUp={() => moveBlock(idx, -1)}
            onMoveDown={() => moveBlock(idx, 1)}
            isFirst={idx === 0}
            isLast={idx === page.blocks.length - 1}
          />
        ))}
      </div>

      {/* Add block */}
      <div className="border-2 border-dashed border-border rounded-xl p-6" data-testid="add-block-area">
        <p className="text-sm text-muted-foreground mb-3 text-center font-medium">Add a content block</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {BLOCK_TYPES.map((bt) => (
            <button
              key={bt.type}
              onClick={() => addBlock(bt.type)}
              className="flex flex-col items-center gap-1.5 p-3 rounded-lg border border-border hover:bg-muted/50 hover:border-primary/30 transition-colors text-center"
              data-testid={`add-block-${bt.type}`}
            >
              <bt.icon className="w-5 h-5 text-primary" />
              <span className="text-xs font-medium">{bt.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
